import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Receipt, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const GenerateLiquidacionModal = ({ dispatch, token, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    precio_flete_usd: '',
    producto: '',
    observacion: ''
  });

  useEffect(() => {
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/dispatches/${dispatch.id}/liquidaciones/preview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreview(response.data);
      
      // Auto-select if only one company available
      const available = response.data.filter(c => c.can_generate);
      if (available.length === 1) {
        setSelectedCompany(available[0]);
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Error al cargar vista previa');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = (companyId) => {
    const company = preview.find(c => c.company_id === companyId);
    setSelectedCompany(company);
    setFormData(prev => ({ ...prev, observacion: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCompany) {
      toast.error('Seleccione una empresa');
      return;
    }
    
    if (!formData.precio_flete_usd || parseFloat(formData.precio_flete_usd) <= 0) {
      toast.error('Ingrese el precio del flete en USD');
      return;
    }
    
    if (selectedCompany.requires_observation && !formData.observacion.trim()) {
      toast.error('Se requiere una observación debido al descuento por pérdida');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/api/dispatches/${dispatch.id}/liquidaciones`,
        {
          dispatch_id: dispatch.id,
          company_id: selectedCompany.company_id,
          precio_flete_usd: parseFloat(formData.precio_flete_usd),
          producto: formData.producto.toUpperCase(),
          observacion: formData.observacion
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Liquidación generada exitosamente');
      onSuccess && onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error('Error generating liquidacion:', error);
      toast.error(error.response?.data?.detail || 'Error al generar liquidación');
    } finally {
      setGenerating(false);
    }
  };

  const availableCompanies = preview.filter(c => c.can_generate);
  const generatedCompanies = preview.filter(c => c.already_generated);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-emerald-50">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-600" />
            <h2 className="text-sm font-bold text-slate-800">
              Generar Liquidación - {dispatch.dispatch_number}
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X size={16} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Cargando...</span>
            </div>
          ) : availableCompanies.length === 0 ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
              <p className="text-sm text-slate-600">
                No hay empresas disponibles para generar liquidación.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                {generatedCompanies.length > 0 
                  ? `Ya se generaron liquidaciones para ${generatedCompanies.length} empresa(s).`
                  : 'Verifique que los camiones tengan todos los datos completos.'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Company Selection */}
              <div>
                <Label className="text-xs">Empresa Transportista *</Label>
                <Select 
                  value={selectedCompany?.company_id || ''} 
                  onValueChange={handleCompanySelect}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Seleccione empresa..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCompanies.map(company => (
                      <SelectItem 
                        key={company.company_id} 
                        value={company.company_id}
                        className="text-xs"
                      >
                        {company.company_name} ({company.valid_trucks} camión{company.valid_trucks !== 1 ? 'es' : ''})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selected company info */}
              {selectedCompany && (
                <div className={`rounded-lg p-3 text-xs ${selectedCompany.has_discount ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{selectedCompany.company_name}</span>
                    <span className="text-slate-500">{selectedCompany.valid_trucks} camión(es) válido(s)</span>
                  </div>
                  {selectedCompany.has_discount && (
                    <div className="flex items-start gap-2 text-amber-700">
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Descuento por pérdida: {selectedCompany.discount_kg} kg</p>
                        <p className="text-[10px]">Se requiere observación explicando el descuento.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Precio Flete USD */}
              <div>
                <Label className="text-xs">Precio Flete USD (por camión) *</Label>
                <Input
                  type="number"
                  value={formData.precio_flete_usd}
                  onChange={(e) => setFormData(prev => ({ ...prev, precio_flete_usd: e.target.value }))}
                  className="h-9 text-xs"
                  placeholder="Ej: 1200"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              {/* Producto */}
              <div>
                <Label className="text-xs">Producto</Label>
                <Input
                  type="text"
                  value={formData.producto}
                  onChange={(e) => setFormData(prev => ({ ...prev, producto: e.target.value }))}
                  className="h-9 text-xs"
                  placeholder="Ej: MAIZ, SOJA, TRIGO"
                />
              </div>

              {/* Observación (obligatoria si hay descuento) */}
              <div>
                <Label className={`text-xs ${selectedCompany?.requires_observation ? 'text-amber-600 font-semibold' : ''}`}>
                  Observación {selectedCompany?.requires_observation ? '(OBLIGATORIA) *' : ''}
                </Label>
                <Textarea
                  value={formData.observacion}
                  onChange={(e) => setFormData(prev => ({ ...prev, observacion: e.target.value }))}
                  className={`text-xs min-h-[80px] ${selectedCompany?.requires_observation ? 'border-amber-300 focus:border-amber-500' : ''}`}
                  placeholder={selectedCompany?.requires_observation 
                    ? 'Explique el motivo del descuento por pérdida...'
                    : 'Observaciones adicionales (opcional)'}
                  required={selectedCompany?.requires_observation}
                />
              </div>

              {/* Already generated info */}
              {generatedCompanies.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 text-xs">
                  <p className="text-slate-500 flex items-center gap-1">
                    <Check size={12} className="text-emerald-500" />
                    Liquidaciones ya generadas: {generatedCompanies.map(c => c.company_name).join(', ')}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button type="button" variant="outline" size="sm" onClick={onClose}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  size="sm" 
                  disabled={generating || !selectedCompany}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {generating ? (
                    <>
                      <Loader2 size={14} className="mr-1 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Receipt size={14} className="mr-1" />
                      Generar Liquidación
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateLiquidacionModal;
